import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmationSent, type Handover } from '@entities/handover';
import { ApiError } from '@shared/api/problem';
import { Button } from '@shared/ui';

import styles from './Handover.module.css';

interface WrittenConfirmationProps {
  state: Handover;
  pending: boolean;
  error: unknown;
  onRequest: () => void;
}

/**
 * Просьба подтвердить обещанное письменно (FE-260, MVP_PRD §39.1).
 *
 * Четыре правила блока:
 *
 * * **кнопка появляется только там, где есть что просить.** Позиция существует
 *   только с цитатой (§42.3): пустой список означает, что автохаус ничего сверх
 *   цены не обещал, — и кнопка, ведущая к `409`, была бы обещанием
 *   несуществующего действия (§2);
 * * **необратимо и в два шага** (CLAUDE.md §8): письмо уходит автохаусу, и
 *   отменить его можно только в переписке, в окне отмены;
 * * **устное обещание не становится подтверждённым** от самой просьбы. Это
 *   сказано на экране прямо: подтверждением станет ответ автохауса, а не факт
 *   отправки письма (§39.1);
 * * **отправленная просьба показывает время и место отмены**, а не исчезает:
 *   пользователь должен видеть, что письмо ушло и где оно лежит.
 *
 * Красной кнопки здесь нет (DESIGN_SYSTEM §5.1): главное действие шага 7 —
 * отметка «купил», и второе красное рядом с ней отняло бы у неё смысл.
 * Отправка просьбы во втором шаге — синий контур (`outline-action`).
 */
export function WrittenConfirmation({
  state,
  pending,
  error,
  onRequest,
}: WrittenConfirmationProps) {
  const { t, i18n } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const locale = i18n.resolvedLanguage ?? 'de';

  const sent = confirmationSent(state);
  const failure = errorText(error, t);

  return (
    <section className={styles.section} aria-labelledby="handover-confirmation">
      <h2 id="handover-confirmation" className={styles.title}>
        {t('handover.confirmation.title')}
      </h2>
      <p className={styles.subtitle}>{t('handover.confirmation.subtitle')}</p>
      {/* Просьба не превращает устное обещание в подтверждённое (§39.1). */}
      <p className={styles.note}>{t('handover.confirmation.notConfirmed')}</p>

      {sent ? (
        <p className={styles.note}>
          {t('handover.confirmation.sent', {
            date: new Date(state.confirmation.requested_at ?? '').toLocaleString(locale),
          })}
        </p>
      ) : null}

      {failure === null ? null : (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}

      {sent ? null : confirming ? (
        <div className={styles.confirmBox} role="group" aria-live="polite">
          <p className={styles.confirmTitle}>{t('handover.confirmation.confirmTitle')}</p>
          <p className={styles.subtitle}>{t('handover.confirmation.confirmText')}</p>
          <div className={styles.actions}>
            <Button variant="outline-action" disabled={pending} onClick={onRequest}>
              {pending ? t('handover.confirmation.pending') : t('handover.confirmation.confirm')}
            </Button>
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('handover.confirmation.action')}
          </Button>
        </div>
      )}
    </section>
  );
}

function errorText(error: unknown, t: (key: string) => string): string | null {
  if (!(error instanceof ApiError)) return null;
  const key = `handover.error.code.${error.code}`;
  const translated = t(key);
  return translated === key ? t('handover.error.text') : translated;
}
